/**
 * Tone system — the single source of colour semantics for the UI kit.
 *
 * Before this existed, every feature header re-declared the same four colour
 * props by hand (`iconBg`, `iconColor`, `accentColor`, `accentHex`), which is
 * why Campaigns, Leads, Audiences and Templates all drifted apart. Components
 * now take a `tone` and read everything from here.
 *
 * The palette is unchanged: `brand` is the app's indigo, and the status tones
 * are the emerald / amber / red / blue / violet families the KPI cards already
 * used. What changed is that each tone now resolves through the design tokens,
 * so a single definition covers light *and* dark.
 */

export type Tone =
    | "brand"
    | "success"
    | "warning"
    | "danger"
    | "info"
    | "violet"
    | "neutral";

export interface ToneTokens {
    /** Tinted background for icon tiles / soft chips */
    surface: string;
    /** Foreground that sits on `surface` */
    fg: string;
    /** Border that pairs with `surface` */
    border: string;
    /** Full-strength fill (accent bars, dots, progress) */
    solid: string;
    /** Text colour when used on the page background */
    text: string;
    /** Complete soft-badge recipe (tint + text + border) */
    badge: string;
    /** Soft chip without a border — for pills that sit inside a bordered row */
    chip: string;
    /** Complete icon-tile recipe */
    tile: string;
    /** 2px accent bar recipe */
    accent: string;
    /** Focus ring recipe */
    ring: string;
}

export const TONES: Record<Tone, ToneTokens> = {
    brand: {
        surface: "bg-brand-subtle",
        fg: "text-brand-on-subtle",
        border: "border-brand-border",
        solid: "bg-brand",
        text: "text-brand",
        badge: "bg-brand-subtle text-brand-on-subtle border border-brand-border",
        chip: "bg-brand-subtle text-brand-on-subtle",
        tile: "bg-brand-subtle text-brand-on-subtle",
        accent: "bg-brand",
        ring: "focus-visible:outline-brand",
    },
    success: {
        surface: "bg-success-subtle",
        fg: "text-success-on-subtle",
        border: "border-success-border",
        solid: "bg-success-solid",
        text: "text-success",
        badge: "bg-success-subtle text-success-on-subtle border border-success-border",
        chip: "bg-success-subtle text-success-on-subtle",
        tile: "bg-success-subtle text-success",
        accent: "bg-success-solid",
        ring: "focus-visible:outline-brand",
    },
    warning: {
        surface: "bg-warning-subtle",
        fg: "text-warning-on-subtle",
        border: "border-warning-border",
        solid: "bg-warning-solid",
        text: "text-warning",
        badge: "bg-warning-subtle text-warning-on-subtle border border-warning-border",
        chip: "bg-warning-subtle text-warning-on-subtle",
        tile: "bg-warning-subtle text-warning",
        accent: "bg-warning-solid",
        ring: "focus-visible:outline-brand",
    },
    danger: {
        surface: "bg-danger-subtle",
        fg: "text-danger-on-subtle",
        border: "border-danger-border",
        solid: "bg-danger-solid",
        text: "text-danger",
        badge: "bg-danger-subtle text-danger-on-subtle border border-danger-border",
        chip: "bg-danger-subtle text-danger-on-subtle",
        tile: "bg-danger-subtle text-danger",
        accent: "bg-danger-solid",
        ring: "focus-visible:outline-brand",
    },
    info: {
        surface: "bg-info-subtle",
        fg: "text-info-on-subtle",
        border: "border-info-border",
        solid: "bg-info-solid",
        text: "text-info",
        badge: "bg-info-subtle text-info-on-subtle border border-info-border",
        chip: "bg-info-subtle text-info-on-subtle",
        tile: "bg-info-subtle text-info",
        accent: "bg-info-solid",
        ring: "focus-visible:outline-brand",
    },
    violet: {
        surface: "bg-violet-subtle",
        fg: "text-violet-on-subtle",
        border: "border-violet-border",
        solid: "bg-violet-solid",
        text: "text-violet",
        badge: "bg-violet-subtle text-violet-on-subtle border border-violet-border",
        chip: "bg-violet-subtle text-violet-on-subtle",
        tile: "bg-violet-subtle text-violet",
        accent: "bg-violet-solid",
        ring: "focus-visible:outline-brand",
    },
    neutral: {
        surface: "bg-surface-sunken",
        fg: "text-content-secondary",
        border: "border-line",
        solid: "bg-content-subtle",
        text: "text-content-secondary",
        badge: "bg-surface-sunken text-content-secondary border border-line",
        chip: "bg-surface-sunken text-content-secondary",
        tile: "bg-surface-sunken text-content-secondary",
        accent: "bg-content-subtle",
        ring: "focus-visible:outline-brand",
    },
};

export const tone = (t: Tone = "brand"): ToneTokens => TONES[t] ?? TONES.brand;

/**
 * Maps the many status vocabularies used across the app (campaigns, tickets,
 * invoices, subscriptions, identities…) onto a single tone.
 *
 * Every legacy status string that the old `support/StatusBadge`,
 * `support/BillingStatusBadge` and the inline campaign badges understood is
 * listed here, so those components can delegate without changing behaviour.
 */
const STATUS_TONE: Record<string, Tone> = {
    // ── positive / terminal-good ──
    active: "success",
    live: "success",
    sent: "success",
    delivered: "success",
    completed: "success",
    complete: "success",
    comp: "success",
    resolved: "success",
    paid: "success",
    succeeded: "success",
    success: "success",
    verified: "success",
    connected: "success",
    healthy: "success",
    implemented: "success",
    qualified: "success",
    converted: "success",
    enabled: "success",
    approved: "success",
    online: "success",

    // ── in-flight ──
    progress: "info",
    in_progress: "info",
    "in-progress": "info",
    processing: "info",
    scheduled: "info",
    sending: "info",
    pending_verification: "info",
    syncing: "info",
    open: "info",
    new: "info",
    running: "info",
    configuring: "info",
    opened: "violet",
    clicked: "brand",
    queued: "neutral",
    contacted: "info",
    available: "info",
    refunded: "info",

    // ── needs attention ──
    pending: "warning",
    paused: "warning",
    partially_completed: "warning",
    pc: "warning",
    warning: "warning",
    review: "warning",
    trial: "warning",
    expiring: "warning",
    past_due: "warning",
    unverified: "warning",
    throttled: "warning",
    retrying: "warning",
    awaiting_review: "warning",
    away: "warning",

    // ── negative ──
    failed: "danger",
    error: "danger",
    terminated: "danger",
    cancelled: "danger",
    canceled: "danger",
    rejected: "danger",
    expired: "danger",
    bounced: "danger",
    suspended: "danger",
    deleted: "danger",
    revoked: "danger",
    conflict: "danger",
    quota_exceeded: "danger",
    lost: "danger",
    offline: "danger",
    disconnected: "danger",

    // ── neutral / inert ──
    draft: "neutral",
    closed: "neutral",
    archived: "neutral",
    inactive: "neutral",
    disabled: "neutral",
    unknown: "neutral",
};

export function toneForStatus(status?: string | null): Tone {
    if (!status) return "neutral";
    const key = String(status).trim().toLowerCase().replace(/\s+/g, "_");
    return STATUS_TONE[key] ?? "neutral";
}

/** "in_progress" → "In Progress" */
export function humanizeStatus(status?: string | null): string {
    if (status === null || status === undefined || status === "") return "Unknown";
    return String(status)
        .replace(/[_-]+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ── Priority and severity ───────────────────────────────────────────────
   A separate vocabulary from status: these describe *how urgent*, not *what
   state*. Five files each had their own colour ramp and they disagreed —
   "high" was red in two and orange in a third, "low" was green in two and
   yellow in another.

   One monotonic ramp, so urgency reads as a scale rather than a set of
   unrelated colours, and `critical` stays visually distinct from `high`:

       critical → danger    high → warning    medium → info    low → success
   ───────────────────────────────────────────────────────────────────────── */

const PRIORITY_TONE: Record<string, Tone> = {
    critical: "danger",
    urgent: "danger",
    blocker: "danger",
    p0: "danger",

    high: "warning",
    p1: "warning",

    medium: "info",
    normal: "info",
    moderate: "info",
    p2: "info",

    low: "success",
    minor: "success",
    p3: "success",
};

export function toneForPriority(priority?: string | null): Tone {
    if (!priority) return "neutral";
    const key = String(priority).trim().toLowerCase().replace(/\s+/g, "_");
    return PRIORITY_TONE[key] ?? "neutral";
}

/**
 * Severity uses the priority ramp, plus the log-level words that mean the
 * same thing (`error` / `warning` / `info`).
 */
export function toneForSeverity(severity?: string | null): Tone {
    if (!severity) return "neutral";
    const key = String(severity).trim().toLowerCase();
    if (key === "error" || key === "fatal") return "danger";
    if (key === "warning" || key === "warn") return "warning";
    if (key === "info" || key === "notice") return "info";
    if (key === "healthy" || key === "ok" || key === "up") return "success";
    return toneForPriority(key);
}

/* ── Measured values ─────────────────────────────────────────────────────
   Percentages and scores banded onto the ramp. Bands are checked in order and
   the first one the value clears wins, so they read top-down like the `if`
   chains they replace.

   Only use this where each band maps to a distinct tone. A four-step ramp that
   needs two separate warm steps (amber *and* orange) cannot be expressed here
   without collapsing two bands into one colour — those stay as they are rather
   than silently losing a level of detail.
   ───────────────────────────────────────────────────────────────────────── */

export interface ToneBand {
    /** Inclusive lower bound */
    atLeast: number;
    tone: Tone;
}

export function toneForMeasure(
    value: number,
    bands: ToneBand[],
    fallback: Tone = "neutral"
): Tone {
    if (!Number.isFinite(value)) return fallback;
    for (const band of bands) {
        if (value >= band.atLeast) return band.tone;
    }
    return fallback;
}
